import { useEffect, useRef } from "react";
import { useRecordClickIn } from "@/lib/api";
import { useLocation } from "wouter";

export function usePageTracking() {
  const recordClickIn = useRecordClickIn();
  const clickInIdRef = useRef<string | null>(null);
  const [location] = useLocation();

  useEffect(() => {
    const pageLoadTime = Date.now();
    let sendTimeOnPageHandler: (() => void) | null = null;
    let visibilityHandler: (() => void) | null = null;
    let timeoutId: ReturnType<typeof setTimeout>;

    timeoutId = setTimeout(() => {
      const urlParams = new URLSearchParams(window.location.search);
      const visitorId = localStorage.getItem("visitorId") || crypto.randomUUID();
      localStorage.setItem("visitorId", visitorId);

      let source = urlParams.get("utm_source") || urlParams.get("source") || null;
      const gclid = urlParams.get("gclid");
      const fbclid = urlParams.get("fbclid");
      const msclkid = urlParams.get("msclkid");

      if (!source) {
        if (gclid) source = "google";
        else if (fbclid) source = "facebook";
        else if (msclkid) source = "bing";
      }

      recordClickIn.mutate({
        source,
        clickId: urlParams.get("clickid") || gclid || fbclid || msclkid || null,
        keyword: urlParams.get("keyword") || urlParams.get("utm_term") || null,
        campaignId: urlParams.get("campaignid") || urlParams.get("utm_campaign") || null,
        adGroup: urlParams.get("adgroupid") || urlParams.get("utm_content") || null,
        visitorId,
        referrer: document.referrer || null,
        landingPage: window.location.pathname + window.location.search,
        userAgent: navigator.userAgent,
      }, {
        onSuccess: (data) => {
          clickInIdRef.current = data.id;

          sendTimeOnPageHandler = () => {
            if (clickInIdRef.current) {
              const timeOnPage = Math.round((Date.now() - pageLoadTime) / 1000);
              const blob = new Blob([JSON.stringify({ timeOnPage })], { type: "application/json" });
              navigator.sendBeacon(
                `/api/tracking/click-in/${clickInIdRef.current}/time`,
                blob
              );
            }
          };

          visibilityHandler = () => {
            if (document.visibilityState === 'hidden' && sendTimeOnPageHandler) {
              sendTimeOnPageHandler();
            }
          };

          window.addEventListener('beforeunload', sendTimeOnPageHandler);
          document.addEventListener('visibilitychange', visibilityHandler);
        }
      });
    }, 100);

    return () => {
      clearTimeout(timeoutId);
      if (sendTimeOnPageHandler) {
        sendTimeOnPageHandler();
        window.removeEventListener('beforeunload', sendTimeOnPageHandler);
      }
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
      }
      clickInIdRef.current = null;
    };
  }, [location]);

  return clickInIdRef;
}
